import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiJson } from '../../../components/apiClient';

const apiProjetos = (path, opts) => apiJson(`/projetos${path}`, opts);

// Remove acentos pra busca não depender de digitar o acento certo
// ("ifageek"/"Ifageek" tem que achar mesmo se o nome tiver acentuação).
const normalizar = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Seletor buscável de projeto (§4 da rodada de correção pós-QA): resolve o
// bug real de não existir, na UI, como vincular retroativamente um
// lançamento financeiro a um projeto. Prioriza os projetos do cliente já
// vinculado ao lançamento, mas permite escolher qualquer projeto do tenant
// (ex.: despesas gerais sem cliente). `onSelecionar(projetoIdOuNull)` já
// dispara a atualização imediata — quem chama decide o efeito colateral
// (PUT no lançamento, refletindo em Financeiro/Projeto/Cliente/Auditoria).
//
// Renderizado via portal em `document.body`, posicionado com `position:
// fixed` a partir do `anchorRef` (o botão "+ Vincular"/"Alterar" que abriu
// o seletor) — bug real encontrado: `.crm-panel` tem `overflow: hidden`
// (pros cantos arredondados), e como o CONTEXTO fica dentro de um Panel, um
// dropdown posicionado por CSS normal (absolute dentro do próprio fluxo)
// ficava cortado assim que a lista de projetos era mais alta que o espaço
// sobrando no Panel — a busca aparecia mas os resultados nunca apareciam,
// mesmo o projeto existindo. Portal escapa de QUALQUER ancestral com
// overflow:hidden ou scroll próprio (Panel, Sheet, etc).
export function VincularProjetoSelector({ anchorRef, clienteId, projetoAtualId, onSelecionar, onFechar, label = 'Vincular projeto' }) {
  const [busca, setBusca] = useState('');
  const [doCliente, setDoCliente] = useState([]);
  const [gerais, setGerais] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [posicao, setPosicao] = useState(null);
  const ref = useRef(null);

  useLayoutEffect(() => {
    const calcular = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const larguraDesejada = 280;
      const espacoDireita = window.innerWidth - rect.left;
      const left = espacoDireita < larguraDesejada ? Math.max(8, window.innerWidth - larguraDesejada - 8) : rect.left;
      setPosicao({ top: rect.bottom + 6, left });
    };
    calcular();
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => {
      window.removeEventListener('resize', calcular);
      window.removeEventListener('scroll', calcular, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    Promise.all([
      clienteId ? apiProjetos(`?cliente_id=${clienteId}`) : Promise.resolve({ status: 'ok', lista: [] }),
      apiProjetos(''),
    ]).then(([resCliente, resTodos]) => {
      if (!ativo) return;
      if (resCliente.status !== 'ok' || resTodos.status !== 'ok') {
        setErro(resTodos.mensagem || resCliente.mensagem || 'Erro ao carregar projetos');
        setCarregando(false);
        return;
      }
      const listaCliente = resCliente.lista || [];
      const idsCliente = new Set(listaCliente.map((p) => p.id));
      setDoCliente(listaCliente);
      setGerais((resTodos.lista || []).filter((p) => !idsCliente.has(p.id)));
      setCarregando(false);
    }).catch(() => { if (ativo) { setErro('Erro de rede ao carregar projetos'); setCarregando(false); } });
    return () => { ativo = false; };
  }, [clienteId]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !anchorRef?.current?.contains(e.target)) onFechar?.();
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [onFechar, anchorRef]);

  const filtrar = (lista) => {
    const termo = normalizar(busca.trim());
    if (!termo) return lista;
    return lista.filter((p) => normalizar(p.nome).includes(termo));
  };

  const doClienteFiltrados = useMemo(() => filtrar(doCliente), [doCliente, busca]);
  const geraisFiltrados = useMemo(() => filtrar(gerais), [gerais, busca]);

  const selecionar = async (projetoId) => {
    setSalvando(true);
    await onSelecionar(projetoId);
    setSalvando(false);
  };

  if (!posicao) return null;

  return createPortal(
    <div
      className="crm-dropdown-menu align-left"
      ref={ref}
      style={{
        position: 'fixed', top: posicao.top, left: posicao.left, right: 'auto',
        minWidth: '280px', maxHeight: '340px', overflowY: 'auto', padding: 'var(--crm-space-2)', zIndex: 1000,
      }}
    >
      <input
        autoFocus
        className="crm-input"
        placeholder="Buscar projeto..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        style={{ marginBottom: 'var(--crm-space-2)' }}
      />
      {erro ? (
        <div className="crm-form-hint" style={{ padding: 'var(--crm-space-2)', color: 'var(--crm-danger)' }}>{erro}</div>
      ) : carregando ? (
        <div className="crm-form-hint" style={{ padding: 'var(--crm-space-2)' }}>Carregando...</div>
      ) : (
        <>
          {projetoAtualId && (
            <button type="button" className="crm-dropdown-item" disabled={salvando} onClick={() => selecionar(null)} style={{ color: 'var(--crm-danger)' }}>
              Remover vínculo com projeto
            </button>
          )}
          {clienteId && (
            <div className="crm-dropdown-section-label">Projetos do cliente</div>
          )}
          {clienteId && doClienteFiltrados.length === 0 && (
            <div className="crm-form-hint" style={{ padding: '0 var(--crm-space-2) var(--crm-space-2)' }}>Nenhum projeto deste cliente</div>
          )}
          {doClienteFiltrados.map((p) => (
            <button key={p.id} type="button" className="crm-dropdown-item" disabled={salvando || p.id === projetoAtualId} onClick={() => selecionar(p.id)}>
              {p.nome}{p.id === projetoAtualId ? ' (atual)' : ''}
            </button>
          ))}
          <div className="crm-dropdown-section-label">{clienteId ? 'Outros projetos' : 'Projetos'}</div>
          {geraisFiltrados.length === 0 && (
            <div className="crm-form-hint" style={{ padding: '0 var(--crm-space-2) var(--crm-space-2)' }}>Nenhum projeto encontrado</div>
          )}
          {geraisFiltrados.map((p) => (
            <button key={p.id} type="button" className="crm-dropdown-item" disabled={salvando || p.id === projetoAtualId} onClick={() => selecionar(p.id)}>
              {p.nome}{p.id === projetoAtualId ? ' (atual)' : ''}
            </button>
          ))}
        </>
      )}
    </div>,
    document.body,
  );
}

export default VincularProjetoSelector;
